import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../auth/AuthProvider';
import { useDeployChatStream } from '../../hooks/useDeployChatStream';
import { useAppDeployPolling } from '../../hooks/useAppDeployPolling';
import { COLORS, FONTS } from '../../styles/tokens';
import { ChatMessageList } from '../../components/chat/ChatMessageList';
import { ChatInput } from '../../components/chat/ChatInput';
import { TypingIndicator } from '../../components/chat/TypingIndicator';
import { UploadZone } from '../../components/chat/UploadZone';
import { DeployActions } from '../../components/chat/DeployActions';
import { postDeploy } from '../../lib/deploy-api';
import type { ChatMessage, DeployStep } from '../../components/chat/types';

/**
 * AI-guided deploy chat page. Handles the "name → upload → deploy → poll"
 * flow; every non-trivial subtask is delegated to an atomic component or hook.
 */
export default function AppDeployChat(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([greetingMessage()]);
  const [input, setInput] = useState('');
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [deploySlug, setDeploySlug] = useState<string | null>(null);
  const [appName, setAppName] = useState('');
  const [step, setStep] = useState<DeployStep>('name');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const appendMessage = useCallback((msg: ChatMessage) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  const updateLastMessage = useCallback((updater: (previous: ChatMessage) => ChatMessage) => {
    setMessages(prev => {
      if (prev.length === 0) return prev;
      const next = [...prev];
      next[next.length - 1] = updater(prev[prev.length - 1]);
      return next;
    });
  }, []);

  const { isStreaming, streamResponse } = useDeployChatStream({
    token, deploySlug,
    onAppend: appendMessage,
    onUpdateLast: updateLastMessage,
  });
  const pollBuildStatus = useAppDeployPolling({ token, append: appendMessage });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const acceptAppName = useCallback((name: string) => {
    setAppName(name);
    setStep('upload');
    setShowUpload(true);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: name },
      {
        role: 'assistant',
        content:
          'Great! Upload your project as a zip file — drag it below or click the upload area. After the build, your app will live at a `<slug>.apps.velocityexplorations.com` URL.',
      },
    ]);
  }, []);

  const handleSend = useCallback(() => {
    const userMsg = input.trim();
    if (!userMsg || isStreaming) return;
    setInput('');
    if (step === 'name') {
      acceptAppName(userMsg);
      return;
    }
    void streamResponse(messages, userMsg);
  }, [input, isStreaming, step, messages, streamResponse, acceptAppName]);

  const acceptFile = useCallback((file: File | undefined) => {
    if (!file || !file.name.endsWith('.zip')) return;
    setZipFile(file);
    appendMessage({
      role: 'assistant',
      content: `Received \`${file.name}\` (${(file.size / 1024 / 1024).toFixed(1)} MB). Click "Deploy" to start the build.`,
    });
  }, [appendMessage]);

  const handleDeploy = useCallback(async () => {
    if (!zipFile || !appName) return;
    setStep('building');
    appendMessage({ role: 'assistant', content: 'Starting build and deploy pipeline...', type: 'progress' });
    try {
      const { ok, data } = await postDeploy(token, { zip: zipFile, name: appName });
      if (!ok || !data.slug) {
        appendMessage({ role: 'assistant', content: `Deploy failed: ${data.error ?? 'unknown error'}`, type: 'error' });
        setStep('upload');
        return;
      }
      setDeploySlug(data.slug);
      appendMessage({
        role: 'assistant',
        content: `Build started! Slug: \`${data.slug}\`\n\nURL: \`${data.appUrl}\`\n\nThis usually takes 2-5 minutes.`,
        type: 'success',
      });
      setStep('done');
      void pollBuildStatus(data.slug);
    } catch {
      appendMessage({ role: 'assistant', content: 'Deploy request failed. Please try again.', type: 'error' });
      setStep('upload');
    }
  }, [zipFile, appName, token, appendMessage, pollBuildStatus]);

  return (
    <div className="vx-chat-height" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', maxWidth: '800px', margin: '0 auto' }}>
      <header className="vx-page-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '12px' }}>
        <h2 style={{ fontFamily: FONTS.heading, fontSize: '18px', letterSpacing: '0.15em', color: colors.text, textTransform: 'uppercase', margin: 0 }}>
          Deploy App
        </h2>
        <button
          onClick={() => navigate('/apps')}
          style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, fontFamily: FONTS.body, fontSize: '12px', cursor: 'pointer' }}
        >
          Back to Apps
        </button>
      </header>

      <ChatMessageList
        messages={messages}
        isDark={isDark}
        colors={colors}
        bottomRef={messagesEndRef}
        isStreaming={isStreaming}
        indicator={<TypingIndicator />}
      />

      {showUpload && !zipFile && (
        <UploadZone
          isDark={isDark}
          borderColor={colors.border}
          textSecondary={colors.textSecondary}
          inputRef={fileInputRef}
          onDrop={event => { event.preventDefault(); acceptFile(event.dataTransfer.files[0]); }}
          onSelect={event => acceptFile(event.target.files?.[0])}
          sizeHint="Max 100MB"
        />
      )}

      {zipFile && step === 'upload' && (
        <DeployActions
          isDark={isDark}
          borderColor={colors.border}
          textSecondary={colors.textSecondary}
          appName={appName}
          onDeploy={handleDeploy}
          onReset={() => setZipFile(null)}
        />
      )}

      <ChatInput
        value={input}
        placeholder={step === 'name' ? 'Enter app name...' : 'Ask me anything...'}
        disabled={isStreaming}
        isDark={isDark}
        colors={colors}
        onChange={setInput}
        onSubmit={handleSend}
      />
    </div>
  );
}

/** Seed message shown when the user first opens the chat. */
function greetingMessage(): ChatMessage {
  return {
    role: 'assistant',
    content: "Welcome! I'll help you deploy your app to Vertex. Let's start — what would you like to name your app?",
  };
}
